import { ChangeDetectorRef, Component, OnInit, PLATFORM_ID, WritableSignal, computed, inject, signal } from '@angular/core';
import { Event, NavigationEnd, NavigationStart, Router, RouterModule } from '@angular/router';
import { INavLink, TSubMenu, ESubMenuType } from './main-navigation.interface';
import { MainNavigationService } from './main-navigation.service';
import { CommonModule, KeyValue, isPlatformBrowser } from '@angular/common';
import { SearchModalService } from '../search-modal/search-modal.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { SubMenuComponent } from './sub-menu/sub-menu.component';
import { SubMenuWhiteboardVideoComponent } from './sub-menu/sub-menu-whiteboard-video/sub-menu-whiteboard-video.component';
import { SubMenuQuickblogComponent } from './sub-menu/sub-menu-quickblog/sub-menu-quickblog.component';
import { SubMenuPrintSolutionsComponent } from './sub-menu/sub-menu-print-solutions/sub-menu-print-solutions.component';
import { SubMenuContentComponent } from './sub-menu/sub-menu-content/sub-menu-content.component';
import { SubMenuAppSolutionsComponent } from './sub-menu/sub-menu-app-solutions/sub-menu-app-solutions.component';
import { SearchInputComponent } from '../search-input/search-input.component';
import { TGoogleCaptchaAssessment } from '../search-modal/search-modal.interface';
import { IDataPayloadMultiple } from 'src/app/core/http/http.interface';

@Component({
  selector: 'app-main-navigation',
  templateUrl: './main-navigation.component.html',
  styleUrl: './main-navigation.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    SearchInputComponent,
    SubMenuComponent,
    SubMenuAppSolutionsComponent,
    SubMenuContentComponent,
    SubMenuPrintSolutionsComponent,
    SubMenuQuickblogComponent,
    SubMenuWhiteboardVideoComponent,
  ],
})
export class MainNavigationComponent implements OnInit {
  protected readonly _router: Router = inject(Router);
  protected readonly _mainNavService: MainNavigationService = inject(MainNavigationService);
  protected readonly _ref: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected readonly _searchModalService: SearchModalService = inject(SearchModalService);
  protected readonly _platformId: object = inject(PLATFORM_ID);

  private readonly _routerEvents$ = toSignal(this._router.events);

  readonly ESubMenuType: typeof ESubMenuType = ESubMenuType;

  mainNavLinks$ = signal<IDataPayloadMultiple<TSubMenu> | null>(null);
  subMenuState$ = this._mainNavService.subMenuState$;
  navIsOpen$: WritableSignal<boolean> = this._mainNavService.mainMenuState$

  constructor() {
    computed(() => {
      this._handleEvents(this._routerEvents$());
    });
  }

  ngOnInit(): void {
    this.mainNavLinks$ = this._mainNavService.navigation$;
  }

  sortMainNavLinks = (a: KeyValue<string,INavLink>, b: KeyValue<string,INavLink>): number => {
    return a.value.order && b.value.order
      ? a.value.order < b.value.order
        ? -1
        : (b.value.order < a.value.order ? 1 : 0)
      : 0;
  }

  toggleNavigation(open: boolean): void {
    this.toggleSubNav(null);
    this._mainNavService.toggleMainMenu(open);
  }

  toggleSubNav(type: ESubMenuType | null): void {
    this._mainNavService.toggleSubNav(type);
  }

  private _handleEvents(event?: Event): void {
    if (event instanceof NavigationStart) {
      this.toggleSubNav(null);
    }

    if (event instanceof NavigationEnd) {
      this._ref.detectChanges();
    }
  }

  isCurrentPath(slug: string): boolean {
    if (!isPlatformBrowser(this._platformId)) {
      return false;
    }

    return (<any>window).location.pathname.match(slug)?.length > 0;
  }

  async openSearchModal() {
    const captchaAssessment: TGoogleCaptchaAssessment = await this._searchModalService.reCaptcha();

    if (captchaAssessment.riskAnalysis.score > 0.3) {
      this._searchModalService.toggleSearchModal(true);
      return;
    }

    this._searchModalService.toggleCaptchaModal(true);
  }
}
